/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import YAML from "yaml";

import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Survey, LoadStatus, Edits } from "../../../types";
import { readYamlFile, getFormattedDate } from "../../../utils";

export interface UseWithSurveys {
  surveys: Record<string, Survey>;
  surveyEdits: Record<string, Edits<Survey>>;
  surveyLoadStatus: LoadStatus;
  loadAllSurveys: () => Promise<Survey[]>;
  loadSurveyFromYaml: (data: File | string) => Promise<Survey>;
  createSurvey: (newData: Partial<Survey>) => Promise<Survey>;
  deleteSurvey: (survey: Survey) => Promise<void>;
  updateSurvey: (newData: Partial<Survey>) => Promise<Survey>;
  isSurveyLaunchable: (survey: Survey) => boolean;
}

export default function useWithSurveys(): UseWithSurveys {
  const { surveys, surveyEdits, surveyLoadStatus } = useAppSelector(
    (state) => state.surveys
  );
  const dispatch = useAppDispatch();

  async function loadAllSurveys(): Promise<Survey[]> {
    if (surveyLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadSurveys()).unwrap();
  }

  async function loadSurveyFromYaml(file: File | string): Promise<Survey> {
    let data;
    if (typeof file === "string") data = YAML.parse(file);
    else data = await readYamlFile(file);
    return {
      id: data.id || "",
      name: data.name || "",
      desc: data.desc || "",
      hidden: Boolean(data.hidden),
      deleted: Boolean(data.deleted),
      createdAt: data.createdAt || getFormattedDate(),
      updatedAt: data.updatedAt || getFormattedDate(),
      questions: data.questions || [],
      specialEventConditions: data.specialEventConditions || [],
    };
  }

  async function createSurvey(data: Partial<Survey>): Promise<Survey> {
    if (!data.name) throw new Error("name required");
    return await dispatch(
      actions.addOrUpdateSurvey({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        createdAt: data.createdAt || getFormattedDate(),
        updatedAt: data.updatedAt || getFormattedDate(),
        questions: data.questions || [],
        specialEventConditions: data.specialEventConditions || [],
      })
    ).unwrap();
  }

  async function deleteSurvey(survey: Survey): Promise<void> {
    await dispatch(actions.deleteSurvey(survey.id));
  }

  async function updateSurvey(data: Partial<Survey>): Promise<Survey> {
    return await dispatch(actions.addOrUpdateSurvey(data)).unwrap();
  }

  function isSurveyLaunchable(survey: Survey): boolean {
    return survey.questions.length > 0;
  }

  return {
    surveys,
    surveyEdits,
    surveyLoadStatus,
    loadAllSurveys,
    loadSurveyFromYaml,
    createSurvey,
    deleteSurvey,
    updateSurvey,
    isSurveyLaunchable,
  };
}
